/**
 * PageHeader component — shared types and Zod prop schema.
 *
 * A top-of-screen "sub page" header: a leading circular back control, a
 * centered title, and an optional trailing circular close (×) control. When no
 * close is present the trailing side reserves the control's width so the title
 * stays optically centered. Presentational and domain-agnostic — the host
 * supplies the title, the localized control labels, and binds the back/close
 * affordances; the component owns no behavior.
 *
 * Scoped to the two "Sub page" variants the Settings screens need. The Figma
 * component matrix also carries an `H1` headline variant, pagination, and voice
 * variants — those are intentionally not built here.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import type { IconName } from '../../elements/icons/registry';
import { sizing } from '../../elements/sizing';

/**
 * Optional trailing close affordance. Its presence (in the catalog data layer)
 * renders the × control; the host binds the press via the platform `onClose`.
 */
export const pageHeaderClosePropsSchema = z.object({
  accessibilityLabel: z.string().min(1),
});

/** Catalog-facing schema — data props only (events live on the platform props). */
export const pageHeaderPropsSchema = z.object({
  title: z.string(),
  backAccessibilityLabel: z.string().min(1),
  close: pageHeaderClosePropsSchema.optional(),
  /**
   * Maximum lines the title may occupy before truncating. Defaults to 1 (the
   * single-line, tail-ellipsized "Sub page" header). Screens with long titles
   * that should wrap rather than truncate pass a higher value (e.g. 2).
   */
  titleNumberOfLines: z.number().int().positive().optional(),
  testID: z.string().optional(),
});

export type PageHeaderClose = z.infer<typeof pageHeaderClosePropsSchema>;
export type PageHeaderProps = z.input<typeof pageHeaderPropsSchema>;

/**
 * Row height + trailing-spacer diameter — the Figma "Sub page" 44dp control.
 * The controls themselves are the shared `Button` (size M), which owns their
 * circular secondary fill, radius, and glyph sizing.
 */
export const PAGE_HEADER_CONTROL_SIZE = sizing.controlButton.md;
/** Title colour. */
export const PAGE_HEADER_TITLE_COLOR = colors.text.primary;

export const PAGE_HEADER_BACK_ICON: IconName = 'left-arrow';
export const PAGE_HEADER_CLOSE_ICON: IconName = 'close';

/** Default analytics action ids for the two controls (host-overridable). */
export const PAGE_HEADER_BACK_ACTION_ID = 'ui.page-header.back';
export const PAGE_HEADER_CLOSE_ACTION_ID = 'ui.page-header.close';
